import axios from "axios";
import { getTenantSubdomain } from "../lib/subdomain";
import { claveLectura, guardarLectura, obtenerLectura, encolarPendiente } from "../lib/offlineDb";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:4000/api";

export const api = axios.create({ baseURL: API_URL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("vetflow_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers["x-tenant-subdomain"] = getTenantSubdomain();
  return config;
});

// Métodos que sí se pueden encolar para reintentarse solos cuando regrese la
// conexión. GET no se encola (no tiene sentido "reintentar una consulta" —
// en su lugar se sirve desde la caché, ver más abajo).
const METODOS_ENCOLABLES = new Set(["post", "put", "patch", "delete"]);

// Ciertas peticiones no tiene sentido "encolarlas para reintentar solas más
// tarde": son acciones que necesitan una respuesta inmediata para redirigir
// al usuario a otro lado (ej. a Stripe a pagar) — no algo que se pueda
// completar en silencio en segundo plano cuando regrese la conexión.
const RUTAS_NO_ENCOLABLES = [/^\/suscripcion\//];
function esNoEncolable(url?: string): boolean {
  if (!url) return false;
  return RUTAS_NO_ENCOLABLES.some((patron) => patron.test(url));
}

api.interceptors.response.use(
  (res) => {
    // Cada GET exitoso se guarda en IndexedDB para poder mostrarlo si más
    // adelante se pierde la conexión. No bloquea la respuesta (fire and
    // forget) — si falla el guardado en caché, no debe afectar la app.
    if ((res.config.method || "get").toLowerCase() === "get") {
      const clave = claveLectura(res.config.url || "", res.config.params);
      guardarLectura(clave, res.data);
    }
    return res;
  },
  async (error) => {
    const config = error.config;
    // Sin `error.response` significa que la petición nunca llegó a recibir
    // contestación del servidor (sin conexión, DNS caído, timeout) — a
    // diferencia de un error real del servidor (400, 500, etc.), que sí trae
    // `error.response` y debe seguir tratándose como error normal más abajo.
    const esFalloDeConexion = !error.response && !!config;

    if (esFalloDeConexion) {
      const metodo = (config.method || "get").toLowerCase();

      if (metodo === "get") {
        const clave = claveLectura(config.url || "", config.params);
        const cache = await obtenerLectura(clave);
        if (cache) {
          // Se resuelve (no se rechaza) con los últimos datos buenos que
          // teníamos guardados, marcados como "_offline" para que la
          // interfaz pueda avisar que quizás no son los más recientes.
          return {
            data: { ...cache.datos, _offline: true, _cacheadoEn: cache.timestamp },
            status: 200,
            statusText: "OK (datos guardados sin conexión)",
            headers: {},
            config,
          };
        }
        // No hay nada guardado para esta consulta — no queda más remedio
        // que dejar que el error siga su curso normal.
      } else if (METODOS_ENCOLABLES.has(metodo) && !esNoEncolable(config.url)) {
        await encolarPendiente({ metodo: metodo as any, url: config.url || "", datos: parsearBody(config.data) });
        window.dispatchEvent(new Event("mascotasvet:pendientes-cambiaron"));
        // Se resuelve como si el servidor hubiera aceptado la petición, para
        // que el formulario/pantalla que la mandó no truene con un error —
        // se le avisa al usuario en otro lado (banner superior) que quedó
        // pendiente de sincronizar.
        return {
          data: { ...(parsearBody(config.data) || {}), _pendienteSync: true },
          status: 202,
          statusText: "Aceptado sin conexión (pendiente de sincronizar)",
          headers: {},
          config,
        };
      }
    }

    const codigo = error.response?.data?.codigo;

    if (error.response?.status === 401) {
      localStorage.removeItem("vetflow_token");
      localStorage.removeItem("vetflow_usuario");
      localStorage.removeItem("vetflow_tenant");
      if (!window.location.pathname.startsWith("/login") && !window.location.pathname.startsWith("/superadmin")) {
        window.location.href = "/login";
      }
    }

    // Si el negocio pasó a INACTIVO mientras el usuario ya tenía sesión abierta,
    // cerramos su sesión y lo mandamos a la pantalla de login con el aviso.
    if (codigo === "NEGOCIO_INACTIVO") {
      localStorage.removeItem("vetflow_token");
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login?inactivo=1";
      }
    }

    return Promise.reject(error);
  }
);

function parsearBody(data: any) {
  if (!data) return data;
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch {
      return data;
    }
  }
  return data;
}

// Cliente separado para el portal del super administrador: no pertenece a
// ningún negocio, así que no manda el header de subdominio, y usa su propio
// token guardado aparte del de los usuarios de negocio.
export const apiSuperAdmin = axios.create({ baseURL: API_URL });

apiSuperAdmin.interceptors.request.use((config) => {
  const token = localStorage.getItem("vetflow_superadmin_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

apiSuperAdmin.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("vetflow_superadmin_token");
      if (!window.location.pathname.startsWith("/superadmin/login")) {
        window.location.href = "/superadmin/login";
      }
    }
    return Promise.reject(error);
  }
);

// Nombres amigables para los campos más comunes que aparecen en los mensajes
// de error de validación (los nombres técnicos vienen tal cual del schema
// del backend, ej. "codigoPostal", "claveProdServ"). Si un campo no está
// aquí, se usa un formateo genérico (separar camelCase en palabras).
const ETIQUETAS_CAMPO: Record<string, string> = {
  nombre: "Nombre",
  email: "Correo electrónico",
  correo: "Correo electrónico",
  telefono: "Teléfono",
  password: "Contraseña",
  contrasena: "Contraseña",
  rfc: "RFC",
  codigoPostal: "Código postal",
  cp: "Código postal",
  calle: "Calle",
  numero: "Número",
  colonia: "Colonia",
  ciudad: "Ciudad",
  estado: "Estado",
  pais: "País",
  subdominio: "Subdominio",
  regimenFiscal: "Régimen fiscal",
  usoCfdiPreferido: "Uso de CFDI",
  claveProdServ: "Clave de producto/servicio (SAT)",
  claveUnidadSat: "Clave de unidad (SAT)",
  objetoImpuesto: "Objeto de impuesto",
  tasaIva: "Tasa de IVA",
  precioVenta: "Precio de venta",
  precioCompra: "Precio de compra",
  stockActual: "Stock actual",
  stockMinimo: "Stock mínimo",
  caducidad: "Fecha de caducidad",
  proveedor: "Proveedor",
  sku: "SKU",
  fecha: "Fecha",
  fechaInicio: "Fecha de inicio",
  fechaFin: "Fecha de fin",
  hora: "Hora",
  titulo: "Título",
  descripcion: "Descripción",
  notas: "Notas",
  cedulaProfesional: "Cédula profesional",
  cedula: "Cédula profesional",
};

function etiquetaCampo(campo: string): string {
  if (!campo) return "Este campo";
  // Los paths anidados (ej. "domicilio.calle") solo interesan por su último
  // segmento para mostrarle al usuario cuál campo del formulario es.
  const ultimo = campo.split(".").pop() || campo;
  if (ETIQUETAS_CAMPO[ultimo]) return ETIQUETAS_CAMPO[ultimo];
  // Formateo genérico: camelCase -> "Camel Case" -> primera letra mayúscula.
  const conEspacios = ultimo.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();
  return conEspacios.charAt(0).toUpperCase() + conEspacios.slice(1);
}

export function mensajeError(error: unknown): string {
  const anyErr = error as any;
  const data = anyErr?.response?.data;

  // El backend manda `detalles: [{ campo, mensaje }]` para errores de
  // validación (campos obligatorios, formato inválido, valores duplicados,
  // etc.) — antes se ignoraba y solo se mostraba el mensaje genérico
  // ("Datos inválidos"), sin decir cuál campo ni por qué. Ahora se arma un
  // mensaje que sí lo dice.
  if (Array.isArray(data?.detalles) && data.detalles.length > 0) {
    const partes = data.detalles
      .map((d: any) => (d?.campo ? `${etiquetaCampo(d.campo)}: ${d.mensaje}` : d?.mensaje))
      .filter(Boolean);
    if (partes.length > 0) {
      const encabezado = data?.error && data.error !== "Datos inválidos" ? `${data.error}. ` : "";
      return `${encabezado}${partes.join(" · ")}`;
    }
  }

  return data?.error ?? anyErr?.message ?? "Ocurrió un error inesperado";
}

export function codigoError(error: unknown): string | undefined {
  const anyErr = error as any;
  return anyErr?.response?.data?.codigo;
}
